import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Container, Table, Button, Alert, Row, Col, Input } from 'reactstrap';
import {useNavigate } from 'react-router-dom'

import {
  getClients,
  deleteClientById,
  downloadClients,
  setDeleteClientById,
  setDownloadClients,
} from '../../redux/actions/client';
import Loading from '../../components/Loading/Loading';
import ConfirmationModal from '../../components/Modal/ConfirmationModal';
import CategorySelectionModal from '../../components/Modal/CategorySelectionModal';
import '../../components/Modal/CategorySelectionModal.scss';
import { hasPermission } from '../../utils/permissions';

function ClientList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const clientState = useSelector((state) => state.client.get);
  const deleteState = useSelector((state) => state.client.deleteById);
  const downloadState = useSelector((state) => state.client.download);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [search, setSearch] = useState('');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);

  const clientList = (search = '') => {
    setLoading(true);
    dispatch(getClients(search));
  };

  const createClient = () => {
    navigate('/client-form');
  };

  const detailClient = (clientId) => {
    navigate(`/client-detail/${clientId}`);
  };

  const editClient = (clientId) => {
    navigate(`/client-form/${clientId}`);
  };

  const deleteClient = (clientId) => {
    setLoading(true);
    dispatch(deleteClientById(clientId));
  };

  const handleSearch = () => {
    clientList(search);
  };

  const handleSearchClear = () => {
    setSearch('');
    clientList();
  };

  const handleDownloadFile = () => {
    setShowCategoryModal(true);
  };

  const handleCategorySelection = (
    categories,
    includeClientsWithoutInterests = false,
  ) => {
    setSelectedCategories(categories);
    setLoading(true);
    dispatch(downloadClients(categories, includeClientsWithoutInterests));
  };

  const handleAlert = (type, message) => {
    setAlertType(type);
    setAlertMessage(message);
    setShowAlert(true);

    // Hide alert after 3 seconds
    const timer = setTimeout(() => setShowAlert(false), 3000);
    return () => clearTimeout(timer);
  };

  const clearReduxState = (actionCreator, delay = 3500) => {
    // Clear update state after 3.5 seconds
    setTimeout(() => {
      dispatch(actionCreator(null));
    }, delay);
  };

  useEffect(() => {
    document.title = 'BGL - Clientes';
    clientList();
  }, []);

  useEffect(() => {
    if (clientState) {
      setData(clientState.data);
      setLoading(false);
    }

    if (deleteState) {
      handleAlert(deleteState.status, deleteState.message);
      setLoading(false);
      clearReduxState(setDeleteClientById);
    }

    if (downloadState) {
      try {
        setLoading(false);

        // Create a blob object with the data
        const blob = new Blob([downloadState], { type: 'text/csv' });

        // Create URL for the blob
        const url = window.URL.createObjectURL(blob);

        // Create download element
        const a = document.createElement('a');
        a.href = url;
        a.download = 'clientes.csv';
        document.body.appendChild(a);
        a.click();

        // Cleanup
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        setLoading(false);
        clearReduxState(setDownloadClients);
      } catch (error) {
        handleAlert('danger', 'Error al descargar el archivo CSV');
        setLoading(false);
        clearReduxState(setDownloadClients);
      }
    }
  }, [clientState, deleteState, downloadState]);

  return loading ? (
    <Loading />
  ) : (
    <>
      <Container>
        <Alert
          color={alertType}
          isOpen={showAlert}
          toggle={() => setShowAlert(false)}
        >
          {alertMessage}
        </Alert>
      </Container>

      <Container className="container-border p-3">
        <article>
          {hasPermission('clients.create') && (
            <Button
              className="float-right mb-2"
              size="sm"
              onClick={() => createClient()}
            >
              <i className="fa fa-plus mr-3"></i>
              <span>Crear cliente</span>
            </Button>
          )}
          {hasPermission('clients.export') && (
            <Button
              className="float-right mb-2"
              size="sm"
              onClick={() => handleDownloadFile()}
              color="success"
            >
              <i className="fa fa-download mr-3"></i>
              <span>
                Descargar datos
                {selectedCategories.length > 0 && (
                  <span className="badge badge-light ml-2">
                    {selectedCategories.length} categorías
                  </span>
                )}
              </span>
            </Button>
          )}
          <Button
            className="float-right mb-2"
            size="sm"
            onClick={() => clientList()}
          >
            <i className="fa fa-sync mr-3"></i>
            <span>Refrescar</span>
          </Button>
          <h2>Lista de Clientes</h2>
        </article>

        <Row className="mb-3">
          <Col>
            <Input
              id="search"
              bsSize="sm"
              type="search"
              className="m-0"
              placeholder="Buscar cliente por nombre, apellido, RUT, teléfono o correo electrónico"
              disabled={loading}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>
          <Col className="p-0">
            <Button
              type="button"
              color="primary"
              size="sm"
              className="m-0"
              onClick={() => handleSearch()}
            >
              <i className="fa fa-search"></i>
            </Button>
            <Button
              type="button"
              color="primary"
              size="sm"
              className="m-0 ml-2"
              disabled={search === ''}
              onClick={() => handleSearchClear()}
            >
              <i className="fa fa-times"></i>
            </Button>
          </Col>
        </Row>

        <div style={{ overflowX: 'auto', width: '100%', overflowY: 'visible' }}>
          <Table hover style={{ minWidth: '800px' }}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre</th>
                <th>Apellido</th>
                <th>RUT</th>
                <th>Teléfono</th>
                <th>Correo Electrónico</th>
                <th>Categorías de Interés</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center">
                    No hay clientes disponibles
                  </td>
                </tr>
              )}

              {data.map((row, index) => {
                return (
                  <tr
                    key={index}
                    onClick={() => detailClient(row.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{row.id}</td>
                    <td>{row.data.firstName || '--'}</td>
                    <td>{row.data.lastName || '--'}</td>
                    <td>{row.data.rut || '--'}</td>
                    <td>{row.data.phone || '--'}</td>
                    <td>{row.data.email || '--'}</td>
                    <td>
                      {row.data.interests && row.data.interests.length > 0 ? (
                        <div>
                          {row.data.interests.map((interest, idx) => (
                            <div key={idx} className="">
                              <span className="badge badge-primary mr-1">
                                {interest.category}
                                {interest.subcategories &&
                                  interest.subcategories.length > 0 && (
                                    <span className="badge badge-light ml-1">
                                      {interest.subcategories.length}
                                    </span>
                                  )}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">Sin categorías</span>
                      )}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <Button
                        className="mr-2"
                        onClick={() => detailClient(row.id)}
                      >
                        <i className="fa fa-eye"></i>
                      </Button>
                      {hasPermission('clients.edit') && (
                        <Button
                          className="mr-2"
                          onClick={() => editClient(row.id)}
                        >
                          <i className="fa fa-edit"></i>
                        </Button>
                      )}
                      {hasPermission('clients.delete') && (
                        <span onClick={(e) => e.stopPropagation()}>
                          <ConfirmationModal
                            btnClass="btn btn-danger"
                            iconClass="fa fa-trash"
                            title="Eliminar el cliente"
                            message="¿Estas seguro que quieres eliminar a este cliente? Esta operación no se podrá deshacer."
                            action={() => deleteClient(row.id)}
                            argument={row.id}
                            successMessage="Eliminar"
                            cancelMessage="Cancelar"
                            btnSuccessState="danger"
                            btnCancelState="secondary"
                          />
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </Container>

      {/* Category Selection Modal */}
      <CategorySelectionModal
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        onConfirm={handleCategorySelection}
        selectedCategories={selectedCategories}
      />
    </>
  );
}

export default ClientList;
